"use server";

import { defineAction } from "@/lib/define-action";
import { can } from "@/lib/access-rules";
import { prisma } from "@/lib/prisma";
import { getSetting, getSettings } from "@/lib/settings";
import { creatorGuard } from "./creator-guard";
import type { TemplateOutline } from "./plan";
import { dayKey } from "./schedule";
import { loadTemplateDoc } from "./template-store";

const CLOSED = /^(archiv|clos|abgeschlossen)/i;

export type ClientOpenPackage = {
  id: number;
  name: string;
  bexioProjectName: string;
  budgetHours: number | null;
  projectCount: number;
};

type NewProjectForm =
  | { ok: false; error: string }
  | {
      ok: true;
      clients: { id: number; name: string; hasBexioProject: boolean; archived: boolean; packages: ClientOpenPackage[] }[];
      templates: TemplateOutline[];
      roles: { id: number; name: string; side: "EVIA" | "CLIENT" }[];
      users: { id: number; name: string }[];
      today: string;
      moneyOn: boolean;
      needsApproval: boolean;
      canCreateClient: boolean;
      defaultRate: string;
      currency: string;
      canSeeMoney: boolean;
    };

/** Everything the "New project" dialog needs, loaded when it opens so no page has to fetch it up front. */
export const loadNewProjectForm = defineAction({
  guard: creatorGuard,
  safe: "Could not open the form.",
  run: async ({ access }): Promise<NewProjectForm> => {
    const [clients, packageRows, templateRows, roles, users, money, rateSettings] = await Promise.all([
      prisma.client.findMany({ where: { deletedAt: null }, orderBy: { name: "asc" }, select: { id: true, name: true, archivedAt: true, _count: { select: { bexioProjects: true } } } }),
      prisma.workPackage.findMany({
        where: { archivedAt: null, bexioProject: { clientId: { not: null } } },
        select: {
          id: true,
          name: true,
          budgetHours: true,
          bexioProject: { select: { clientId: true, name: true, status: true } },
          hubProjects: { where: { deletedAt: null }, select: { id: true } },
        },
        orderBy: { name: "asc" },
      }),
      prisma.projectTemplate.findMany({ where: { deletedAt: null }, orderBy: { name: "asc" }, select: { id: true } }),
      prisma.roleLabel.findMany({ orderBy: { name: "asc" }, select: { id: true, name: true, side: true } }),
      prisma.user.findMany({ where: { kind: "MEMBER", status: "ACTIVE", deletedAt: null, blockedAt: null }, orderBy: { name: "asc" }, select: { id: true, name: true } }),
      getSetting("pm.money_enabled"),
      getSettings(["pm.default_rate", "pm.currency"]),
    ]);
    const packagesByClient = new Map<number, ClientOpenPackage[]>();
    // An archived client's Bexio project is archived too; its budgets stay offered because the new project reactivates it.
    const archivedClients = new Set(clients.filter((c) => c.archivedAt).map((c) => c.id));
    for (const p of packageRows) {
      const clientId = p.bexioProject.clientId;
      if (!clientId || (CLOSED.test(p.bexioProject.status ?? "") && !archivedClients.has(clientId))) continue;
      const list = packagesByClient.get(clientId) ?? [];
      list.push({
        id: p.id,
        name: p.name,
        bexioProjectName: p.bexioProject.name,
        budgetHours: p.budgetHours === null ? null : Number(p.budgetHours),
        projectCount: p.hubProjects.length,
      });
      packagesByClient.set(clientId, list);
    }
    const templates = (await Promise.all(templateRows.map((t) => loadTemplateDoc(t.id)))).flatMap((t) => (t ? [{ id: t.id, name: t.doc.name, buckets: t.doc.buckets }] : []));
    return {
      ok: true,
      clients: clients.map((c) => ({
        id: c.id,
        name: c.name,
        hasBexioProject: c._count.bexioProjects > 0,
        archived: c.archivedAt !== null,
        packages: packagesByClient.get(c.id) ?? [],
      })),
      templates,
      roles,
      users: users.map((u) => ({ id: u.id, name: u.name ?? "Unnamed user" })),
      today: dayKey(new Date()),
      moneyOn: money === "true" && can(access, "pm:money_view"),
      needsApproval: !can(access, "projects:approve") && !can(access, "projects:start"),
      canCreateClient: can(access, "clients:manage"),
      defaultRate: rateSettings["pm.default_rate"] ?? "140",
      currency: rateSettings["pm.currency"] ?? "USD",
      canSeeMoney: can(access, "pm:money_view"),
    };
  },
});
